/**
 * Expandable operation panel for the toolbar.
 *
 * Shows searchable, tabbed grid of operations that can be clicked or dragged
 * onto the canvas. Manages its own search, tab, and hover state.
 */

import React, { useState, useCallback, useEffect, useRef, useMemo } from "react";
import {
  tokens,
  Button,
  Tooltip,
  mergeClasses,
  Input,
  Text,
  TabList,
  Tab,
} from "@fluentui/react-components";
import {
  PlayRegular,
  FlashRegular,
  BranchForkFilled,
  ArrowSplitFilled,
  ArrowRepeatAllFilled,
  ArrowSyncFilled,
  BotFilled,
  SearchRegular,
  DismissRegular,
  OrganizationHorizontalRegular,
  PlugConnectedRegular,
  WrenchRegular,
  NoteAddRegular,
  GridDotsRegular,
} from "@fluentui/react-icons";
import {
  CONTROL_FLOW_OPERATIONS,
  AGENT_OPERATIONS,
  INLINE_CODE_OPERATIONS,
  type OperationDefinition,
} from "@/types/operations";
import { setOperationDragData } from "@/hooks/useToolboxDragDrop";
import { useSearchOperations } from "@/hooks/useOperations";
import { useFeatureFlag } from "@/hooks/useFeatureFlag";
import { BUILTIN_MANIFESTS } from "@/data/templates";
import { TemplateCard } from "../../Templates/TemplateCard";
import { HighlightText } from "../../panels/HighlightText";
import { type AgentSelection } from "../../panels/AgentTypeView";
import { useOperationPanelStyles } from "./operationPanel.styles";
import type { PanelTab } from "../DockToolbar/dockToolbar.types";
import {
  AgentVariantIcon,
  buildAgentOperationFromSelection,
  useAgentBadgeClass,
  type AgentVariant,
} from "../../helpers/agentVariant";

const ALL_OPERATIONS = [...CONTROL_FLOW_OPERATIONS, ...AGENT_OPERATIONS, ...INLINE_CODE_OPERATIONS];

// Inject stagger keyframes once (module-level, not per-render)
if (typeof document !== "undefined") {
  const styleId = "dock-panel-card-stagger";
  if (!document.getElementById(styleId)) {
    const style = document.createElement("style");
    style.id = styleId;
    style.textContent = `
@keyframes dockPanelCardIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}
@media (prefers-reduced-motion: reduce) {
  @keyframes dockPanelCardIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
}`;
    document.head.appendChild(style);
  }
}

const getOperationIcon = (operationId: string) => {
  switch (operationId) {
    case "condition":
      return <BranchForkFilled aria-hidden="true" />;
    case "switch":
      return <ArrowSplitFilled aria-hidden="true" />;
    case "foreach":
      return <ArrowRepeatAllFilled aria-hidden="true" />;
    case "until":
      return <ArrowSyncFilled aria-hidden="true" />;
    case "agent":
      return <BotFilled aria-hidden="true" />;
    default:
      return <PlayRegular aria-hidden="true" />;
  }
};

export interface OperationPanelProps {
  isExpanded: boolean;
  onClose: () => void;
  onOperationSelect?: (operation: OperationDefinition) => void;
  onBrowseActions?: () => void;
  onBrowseTriggers?: () => void;
  onBrowseMCP?: () => void;
  onBrowseSubworkflow?: () => void;
  onBrowseTemplates?: () => void;
  hasTrigger?: boolean;
  onAddNote?: () => void;
}

export const OperationPanel: React.FC<OperationPanelProps> = ({
  isExpanded,
  onClose,
  onOperationSelect,
  onBrowseActions,
  onBrowseTriggers,
  onBrowseMCP,
  onBrowseSubworkflow,
  onBrowseTemplates,
  hasTrigger = false,
  onAddNote,
}) => {
  const styles = useOperationPanelStyles();
  const agentBadgeClass = useAgentBadgeClass();
  const [activeTab, setActiveTab] = useState<PanelTab>("control");
  const [searchQuery, setSearchQuery] = useState("");
  const { data: azureSearchResults, isLoading: isSearching } = useSearchOperations(searchQuery, "action");
  const [hoveredDescription, setHoveredDescription] = useState<string | null>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const panelContentRef = useRef<HTMLDivElement>(null);
  const dragPreviewRef = useRef<HTMLDivElement | null>(null);
  const prefersReducedMotion = typeof window !== "undefined"
    && typeof window.matchMedia === "function"
    && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  // Cleanup drag preview on unmount
  useEffect(() => {
    return () => {
      if (dragPreviewRef.current?.parentNode) {
        dragPreviewRef.current.parentNode.removeChild(dragPreviewRef.current);
        dragPreviewRef.current = null;
      }
    };
  }, []);

  // Focus search when panel opens
  useEffect(() => {
    if (isExpanded) {
      const timer = setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isExpanded]);

  // Filter operations by search and tab
  const filteredOperations = useMemo(() => {
    if (!searchQuery.trim()) {
      if (activeTab === "control") {
        return CONTROL_FLOW_OPERATIONS;
      } else if (activeTab === "agent") {
        return [];
      } else {
        return [];
      }
    }
    const query = searchQuery.toLowerCase();
    const builtInMatches = ALL_OPERATIONS.filter(
      (op) =>
        op.name.toLowerCase().includes(query) ||
        op.description?.toLowerCase().includes(query),
    );
    const azureOps = azureSearchResults ?? [];
    const seen = new Set(builtInMatches.map((op) => op.id));
    const combined = [...builtInMatches];
    for (const op of azureOps) {
      if (!seen.has(op.id)) {
        combined.push(op);
        seen.add(op.id);
      }
    }
    return combined;
  }, [searchQuery, activeTab, azureSearchResults]);

  const handleOperationClick = useCallback(
    (operation: OperationDefinition) => {
      onOperationSelect?.(operation);
      onClose();
      setSearchQuery("");
    },
    [onOperationSelect, onClose],
  );

  const handleAgentSelectionSubmit = useCallback(
    (selection: AgentSelection) => {
      const agentOp = AGENT_OPERATIONS[0];
      if (!agentOp) return;
      onOperationSelect?.(buildAgentOperationFromSelection(agentOp, selection));
      onClose();
      setSearchQuery("");
    },
    [onOperationSelect, onClose],
  );

  const handleAddHostedAgent = useCallback(() => {
    const agentOp = AGENT_OPERATIONS[0];
    if (!agentOp) return;
    const baseInputs = (agentOp.defaultInputs ?? {}) as Record<string, unknown>;
    onOperationSelect?.({
      ...agentOp,
      defaultInputs: {
        ...baseInputs,
        agentModelType: "FoundryHostedAgent",
      },
    });
    onClose();
    setSearchQuery("");
  }, [onOperationSelect, onClose]);

  const handleDragStart = useCallback(
    (e: React.DragEvent, operation: OperationDefinition) => {
      setOperationDragData(e, operation);

      if (dragPreviewRef.current?.parentNode) {
        dragPreviewRef.current.parentNode.removeChild(dragPreviewRef.current);
      }

      const dragPreview = document.createElement("div");
      dragPreview.style.cssText = `
        position: absolute;
        top: -1000px;
        left: -1000px;
        padding: 8px 12px;
        background: ${operation.brandColor ?? "#0078d4"};
        color: white;
        border-radius: 4px;
        font-size: 13px;
        font-weight: 500;
        white-space: nowrap;
        box-shadow: 0 2px 8px rgba(0,0,0,0.2);
      `;
      dragPreview.textContent = operation.name;
      document.body.appendChild(dragPreview);
      dragPreviewRef.current = dragPreview;
      e.dataTransfer.setDragImage(dragPreview, 0, 0);

      setTimeout(() => {
        if (dragPreviewRef.current?.parentNode) {
          dragPreviewRef.current.parentNode.removeChild(dragPreviewRef.current);
          dragPreviewRef.current = null;
        }
      }, 0);
    },
    [],
  );

  const handleOperationKeyDown = useCallback(
    (e: React.KeyboardEvent, operation?: OperationDefinition) => {
      if (e.key === "Enter" || e.key === " ") {
        if (operation) {
          e.preventDefault();
          e.stopPropagation();
          handleOperationClick(operation);
        }
        // Browse cards: let native button click handle Enter/Space
        return;
      }

      if (!["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"].includes(e.key)) {
        return;
      }

      e.preventDefault();
      e.stopPropagation();
      const container = panelContentRef.current;
      if (!container) return;

      const buttons = Array.from(
        container.querySelectorAll("button"),
      ) as HTMLButtonElement[];
      const currentIndex = buttons.findIndex((btn) => btn === e.currentTarget);
      if (currentIndex === -1) return;

      const containerWidth = container.clientWidth;
      const buttonWidth = buttons[0]?.offsetWidth || 100;
      const gap = 8;
      const columns = Math.floor((containerWidth + gap) / (buttonWidth + gap)) || 1;

      let nextIndex = currentIndex;
      switch (e.key) {
        case "ArrowRight":
          nextIndex = Math.min(currentIndex + 1, buttons.length - 1);
          break;
        case "ArrowLeft":
          nextIndex = Math.max(currentIndex - 1, 0);
          break;
        case "ArrowDown":
          nextIndex = Math.min(currentIndex + columns, buttons.length - 1);
          break;
        case "ArrowUp":
          if (currentIndex - columns < 0) {
            // First row — move focus back to search input
            searchInputRef.current?.focus();
            return;
          }
          nextIndex = Math.max(currentIndex - columns, 0);
          break;
      }

      buttons[nextIndex]?.focus();
    },
    [handleOperationClick],
  );

  /** Returns onMouseEnter/Leave + onFocus/Blur handlers for hover description */
  const hoverDescriptionProps = useCallback(
    (description: string) => ({
      onMouseEnter: () => setHoveredDescription(description),
      onMouseLeave: () => setHoveredDescription(null),
      onFocus: () => setHoveredDescription(description),
      onBlur: () => setHoveredDescription(null),
    }),
    [],
  );

  const handleBrowseActionsClick = useCallback(() => {
    onBrowseActions?.();
    onClose();
  }, [onBrowseActions, onClose]);

  const handleBrowseTriggersClick = useCallback(() => {
    onBrowseTriggers?.();
    onClose();
  }, [onBrowseTriggers, onClose]);

  const handleBrowseMCPClick = useCallback(() => {
    onBrowseMCP?.();
    onClose();
  }, [onBrowseMCP, onClose]);

  const handleBrowseSubworkflowClick = useCallback(() => {
    (onBrowseSubworkflow ?? onBrowseActions)?.();
    onClose();
  }, [onBrowseSubworkflow, onBrowseActions, onClose]);

  const handleBrowseTemplatesClick = useCallback(() => {
    onBrowseTemplates?.();
    onClose();
  }, [onBrowseTemplates, onClose]);

  // Determine what to show in panel based on tab
  const normalizedQuery = searchQuery.toLowerCase();
  const showBrowseActions = activeTab === "actions" || normalizedQuery.includes("action");
  const showBrowseTriggers =
    (activeTab === "triggers" && !hasTrigger) ||
    (normalizedQuery.includes("trigger") && !hasTrigger);
  const showBrowseMCP = activeTab === "tools" || normalizedQuery.includes("mcp");
  const showSubWorkflow =
    activeTab === "tools" ||
    normalizedQuery.includes("workflow") ||
    normalizedQuery.includes("sub");
  const showAddNote = activeTab === "tools" || normalizedQuery.includes("note");
  const showBrowseTemplates =
    activeTab === "templates" ||
    normalizedQuery.includes("template");
  const showWorkflowAgent = activeTab === "agent" || normalizedQuery.includes("agent");
  const showCodingAgent = activeTab === "agent" || normalizedQuery.includes("agent") || normalizedQuery.includes("coding");
  const isHostedAgentEnabled = useFeatureFlag("hostedAgents");
  const showHostedAgent = isHostedAgentEnabled && activeTab === "agent" && !searchQuery;

  // Compute stagger indices for panel cards
  let _si = 0;
  const browseActionsIdx = showBrowseActions ? _si++ : -1;
  const browseTriggersIdx = showBrowseTriggers ? _si++ : -1;
  const subWorkflowIdx = showSubWorkflow ? _si++ : -1;
  const browseMCPIdx = showBrowseMCP ? _si++ : -1;
  const browseTemplatesIdx = showBrowseTemplates && onBrowseTemplates ? _si++ : -1;
  const addNoteIdx = showAddNote && onAddNote ? _si++ : -1;
  const workflowAgentIdx = showWorkflowAgent ? _si++ : -1;
  const foundryAgentIdx = showWorkflowAgent ? _si++ : -1;
  const codingAgentIdx = showCodingAgent ? _si++ : -1;
  const hostedAgentIdx = showHostedAgent ? _si++ : -1;
  const opStartIdx = _si;

  const getStaggerStyle = (index: number): React.CSSProperties => ({
    animation: isExpanded && !prefersReducedMotion
      ? `dockPanelCardIn 300ms ease-out ${Math.min(index * 60, 300)}ms backwards`
      : undefined,
    opacity: isExpanded ? undefined : 0,
  });

  return (
    <div
      className={mergeClasses(
        styles.panel,
        !isExpanded && styles.panelCollapsed,
      )}
      role="region"
      aria-label="Add operations panel"
      aria-hidden={!isExpanded}
      // @ts-expect-error inert is a valid HTML attribute but not yet in React types
      inert={!isExpanded ? "" : undefined}
    >
      <div className={styles.panelInner}>
        {/* Panel Header with Search */}
        <div className={styles.panelHeader}>
          <SearchRegular aria-hidden="true" />
          <Input
            input={{ ref: searchInputRef, "aria-label": "Search operations" }}
            className={styles.searchInput}
            placeholder="Search operations..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "ArrowDown") {
                e.preventDefault();
                const container = panelContentRef.current;
                if (!container) return;
                const firstButton = container.querySelector("button") as HTMLButtonElement | null;
                firstButton?.focus();
              }
            }}
            appearance="underline"
            size="medium"
          />
          <span className={styles.shortcutHint}>Esc</span>
          <Tooltip content="Close (Esc)" relationship="label">
            <Button
              className={styles.closeButton}
              appearance="subtle"
              icon={<DismissRegular />}
              onClick={onClose}
              aria-label="Close panel"
            />
          </Tooltip>
        </div>

        {/* Tab Navigation */}
        {!searchQuery && (
          <TabList
            className={styles.tabList}
            selectedValue={activeTab}
            onTabSelect={(_, data) => {
              if (data.value === "triggers" && hasTrigger) return;
              setActiveTab(data.value as PanelTab);
            }}
            size="small"
          >
            <Tab value="actions" icon={<PlayRegular />}>
              Actions
            </Tab>
            <Tooltip
              content={
                hasTrigger
                  ? "Workflow already has a trigger"
                  : "Add a trigger to start your workflow"
              }
              relationship="description"
            >
              <Tab
                value="triggers"
                icon={<FlashRegular />}
                disabled={hasTrigger}
                aria-disabled={hasTrigger}
              >
                Triggers
              </Tab>
            </Tooltip>
            <Tab value="control" icon={<BranchForkFilled />}>
              Control
            </Tab>
            <Tab value="agent" icon={<BotFilled />}>
              Agent
            </Tab>
            {onBrowseTemplates && (
              <Tab value="templates" icon={<GridDotsRegular />}>
                Templates
              </Tab>
            )}
            <Tab value="tools" icon={<WrenchRegular />}>
              Tools
            </Tab>
          </TabList>
        )}

        {/* Screen reader result count announcement */}
        {searchQuery.trim() && (
          <div aria-live="polite" role="status" className={styles.srOnly}>
            {isSearching
              ? "Searching..."
              : `${filteredOperations.length} operation${filteredOperations.length !== 1 ? "s" : ""} found`}
          </div>
        )}

        {/* Panel Content */}
        <div ref={panelContentRef} className={styles.panelContent} role="group" aria-label="Available operations">
          {/* Browse Actions card */}
          {showBrowseActions && (
            <button
              type="button"
              className={styles.browseCard}
              onClick={handleBrowseActionsClick}
              onKeyDown={(e) => handleOperationKeyDown(e)}
              {...hoverDescriptionProps("Explore all available actions and connectors")}
              aria-label="Browse all available actions"
              style={getStaggerStyle(browseActionsIdx)}
            >
              <div
                className={styles.browseIcon}
                style={{
                  backgroundColor: tokens.colorBrandBackground,
                  color: "white",
                }}
              >
                <PlayRegular aria-hidden="true" />
              </div>
              <Text className={styles.browseText}>Browse All</Text>
            </button>
          )}

          {/* Browse Triggers card */}
          {showBrowseTriggers && (
            <button
              type="button"
              className={styles.browseCard}
              onClick={handleBrowseTriggersClick}
              onKeyDown={(e) => handleOperationKeyDown(e)}
              {...hoverDescriptionProps("Choose an event that starts your workflow")}
              aria-label="Browse all available triggers"
              style={getStaggerStyle(browseTriggersIdx)}
            >
              <div
                className={styles.browseIcon}
                style={{
                  backgroundColor: tokens.colorPaletteYellowBackground3,
                  color: "white",
                }}
              >
                <FlashRegular aria-hidden="true" />
              </div>
              <Text className={styles.browseText}>Browse All</Text>
            </button>
          )}

          {/* Sub-workflow card */}
          {showSubWorkflow && (
            <button
              type="button"
              className={styles.browseCard}
              onClick={handleBrowseSubworkflowClick}
              onKeyDown={(e) => handleOperationKeyDown(e)}
              {...hoverDescriptionProps("Run another workflow as a step in this one")}
              aria-label="Add sub-workflow"
              style={getStaggerStyle(subWorkflowIdx)}
            >
              <div
                className={styles.browseIcon}
                style={{
                  backgroundColor: tokens.colorPaletteGreenBackground3,
                  color: "white",
                }}
              >
                <OrganizationHorizontalRegular aria-hidden="true" />
              </div>
              <Text className={styles.browseText}>Sub-workflow</Text>
            </button>
          )}

          {/* MCP Tools card */}
          {showBrowseMCP && (
            <button
              type="button"
              className={styles.browseCard}
              onClick={handleBrowseMCPClick}
              onKeyDown={(e) => handleOperationKeyDown(e)}
              {...hoverDescriptionProps("Connect to external tools via Model Context Protocol")}
              aria-label="Browse MCP tools"
              style={getStaggerStyle(browseMCPIdx)}
            >
              <div
                className={styles.browseIcon}
                style={{ backgroundColor: tokens.colorNeutralForeground3, color: tokens.colorNeutralForegroundOnBrand }}
              >
                <PlugConnectedRegular aria-hidden="true" />
              </div>
              <Text className={styles.browseText}>MCP Tools</Text>
            </button>
          )}

          {/* Templates — inline grid on templates tab, browse card on search */}
          {showBrowseTemplates && onBrowseTemplates && (
            activeTab === "templates" && !searchQuery ? (
              <div
                className={styles.templateGrid}
                style={getStaggerStyle(browseTemplatesIdx)}
              >
                {BUILTIN_MANIFESTS.map((t) => (
                  <TemplateCard
                    key={t.id}
                    template={t}
                    onClick={() => {
                      onBrowseTemplates();
                    }}
                    variant="full"
                  />
                ))}
                <button
                  type="button"
                  className={styles.browseAllLink}
                  onClick={handleBrowseTemplatesClick}
                >
                  <Text size={200} style={{ color: tokens.colorBrandForeground1 }}>
                    Browse all templates →
                  </Text>
                </button>
              </div>
            ) : (
              <button
                type="button"
                className={styles.browseCard}
                onClick={handleBrowseTemplatesClick}
                {...hoverDescriptionProps("Browse workflow templates")}
                aria-label="Browse workflow templates"
                style={getStaggerStyle(browseTemplatesIdx)}
              >
                <div
                  className={styles.browseIcon}
                  style={{ backgroundColor: tokens.colorBrandBackground, color: tokens.colorNeutralForegroundOnBrand }}
                >
                  <GridDotsRegular aria-hidden="true" />
                </div>
                <Text className={styles.browseText}>Templates</Text>
              </button>
            )
          )}

          {/* Agent type browse cards — each variant gets its own card with its
              own glyph and badge color, matching the picker and node header. */}
          {([
            {
              variant: 'native',
              show: showWorkflowAgent,
              staggerIdx: workflowAgentIdx,
              label: 'Workflow agent',
              ariaLabel: 'Add a workflow agent',
              hoverDesc: 'Build a custom agent natively in Logic Apps',
            },
            {
              variant: 'foundry',
              show: showWorkflowAgent,
              staggerIdx: foundryAgentIdx,
              label: 'Foundry agent',
              ariaLabel: 'Add a Foundry prompt agent',
              hoverDesc: 'Declarative agent defined through prompts and tools',
            },
            {
              variant: 'coding',
              show: showCodingAgent,
              staggerIdx: codingAgentIdx,
              label: 'Coding agent',
              ariaLabel: 'Add a coding agent',
              hoverDesc: 'Runs in a sandboxed harness with repos and file outputs',
            },
          ] as const).filter((c) => c.show).map((c) => {
            const variant = c.variant as AgentVariant;
            return (
              <button
                key={c.variant}
                type="button"
                className={styles.browseCard}
                onClick={() => handleAgentSelectionSubmit({ variant })}
                onKeyDown={(e) => handleOperationKeyDown(e)}
                {...hoverDescriptionProps(c.hoverDesc)}
                aria-label={c.ariaLabel}
                style={getStaggerStyle(c.staggerIdx)}
              >
                <div className={mergeClasses(styles.browseIcon, agentBadgeClass(variant))}>
                  <AgentVariantIcon variant={variant} />
                </div>
                <Text className={styles.browseText}>{c.label}</Text>
              </button>
            );
          })}

          {/* Hosted Agent card */}
          {showHostedAgent && (
            <button
              type="button"
              className={styles.browseCard}
              onClick={handleAddHostedAgent}
              onKeyDown={(e) => handleOperationKeyDown(e)}
              {...hoverDescriptionProps("Use a pre-provisioned hosted agent container")}
              aria-label="Add hosted agent"
              style={getStaggerStyle(hostedAgentIdx)}
            >
              <div className={mergeClasses(styles.browseIcon, agentBadgeClass("hosted"))}>
                <AgentVariantIcon variant="hosted" />
              </div>
              <Text className={styles.browseText}>Hosted Agent</Text>
            </button>
          )}

          {/* Add Note card */}
          {showAddNote && onAddNote && (
            <button
              type="button"
              className={styles.browseCard}
              onClick={() => {
                onAddNote();
                onClose();
              }}
              onKeyDown={(e) => handleOperationKeyDown(e)}
              {...hoverDescriptionProps("Add a sticky note to the canvas for documentation")}
              aria-label="Add a note"
              style={getStaggerStyle(addNoteIdx)}
            >
              <div
                className={styles.browseIcon}
                style={{ backgroundColor: tokens.colorPaletteYellowBackground2, color: tokens.colorNeutralForeground1 }}
              >
                <NoteAddRegular aria-hidden="true" />
              </div>
              <Text className={styles.browseText}>Note</Text>
            </button>
          )}

          {/* Operations */}
          {filteredOperations.map((operation, index) => (
            <button
              key={operation.id}
              type="button"
              className={styles.operationCard}
              onClick={() => handleOperationClick(operation)}
              onKeyDown={(e) => handleOperationKeyDown(e, operation)}
              {...hoverDescriptionProps(operation.description || `Add ${operation.name} to workflow`)}
              draggable
              onDragStart={(e) => handleDragStart(e, operation)}
              aria-label={`Add ${operation.name}`}
              aria-description={
                operation.description || `Add ${operation.name} to workflow`
              }
              style={getStaggerStyle(opStartIdx + index)}
            >
              <div
                className={styles.operationIcon}
                style={{
                  backgroundColor:
                    operation.brandColor ?? tokens.colorNeutralBackground3,
                }}
              >
                {operation.icon && operation.icon.startsWith("http") ? (
                  <img
                    src={operation.icon}
                    alt=""
                    className={styles.connectorIconImg}
                  />
                ) : (
                  getOperationIcon(operation.id)
                )}
              </div>
              <HighlightText
                text={operation.name}
                query={searchQuery}
                className={styles.operationName}
              />
            </button>
          ))}

          {/* Loading skeleton */}
          {isSearching && searchQuery.trim() && filteredOperations.length === 0 &&
            Array.from({ length: 4 }).map((_, i) => (
              <div key={`skeleton-${i}`} className={styles.skeletonCard} aria-hidden="true">
                <div className={styles.skeletonIcon} />
                <div className={styles.skeletonText} />
              </div>
            ))
          }

          {/* Empty state — only when search completed with no results */}
          {!isSearching &&
            filteredOperations.length === 0 &&
            searchQuery.trim() &&
            !showBrowseActions &&
            !showBrowseTriggers &&
            !showBrowseMCP &&
            !showSubWorkflow &&
            !showBrowseTemplates &&
            !showAddNote &&
            activeTab !== "agent" && (
              <div className={styles.emptyState}>
                <Text>No operations found</Text>
                <Text size={200} style={{ color: tokens.colorNeutralForeground3, marginTop: tokens.spacingVerticalXS }}>
                  Try different keywords or browse connectors
                </Text>
              </div>
            )}
        </div>
        {/* Description bar */}
        {isExpanded && (
          <div
            className={styles.descriptionBar}
            style={{
              opacity: hoveredDescription ? 1 : 0.6,
              transform: hoveredDescription ? "translateY(0)" : "translateY(2px)",
            }}
            aria-live="polite"
            role="status"
          >
            <Text size={200} className={styles.descriptionBarText}>
              {hoveredDescription || "Hover over an operation for details"}
            </Text>
          </div>
        )}
      </div>
    </div>
  );
};
